import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen } from 'lucide-react';
import SEO from '../components/SEO';

const Glossaire = () => {
  const glossaryData = {
    macronutriments: {
      title: "Les macronutriments",
      intro: "Les macronutriments sont les nutriments principaux qui apportent de l'énergie au corps.",
      terms: [
        {
          name: "Protéines",
          definition: "Essentielles pour les muscles, les tissus et l'équilibre hormonal. Elles permettent la construction et la réparation du corps."
        },
        {
          name: "Lipides (graisses)",
          definition: "Indispensables au fonctionnement des cellules et des hormones. Ils protègent les organes et facilitent l'absorption des vitamines."
        },
        {
          name: "Glucides (sucres)",
          definition: "Principale source d'énergie du corps. Ils alimentent le cerveau, les muscles et toutes les cellules."
        }
      ]
    },
    micronutriments: {
      title: "Les micronutriments",
      intro: "Les micronutriments sont des éléments essentiels en petites quantités, mais indispensables au bon fonctionnement du corps.",
      terms: [
        {
          name: "Vitamines",
          definition: "Molécules essentielles au bon fonctionnement du corps : énergie, immunité, peau, hormones. Exemples : vitamine D, B, C, E."
        },
        {
          name: "Minéraux",
          definition: "Éléments comme le calcium, le magnésium ou le fer, nécessaires aux os, aux muscles et au transport de l'oxygène."
        },
        {
          name: "Oligoéléments",
          definition: "Minéraux en très petite quantité mais essentiels : zinc, sélénium, iode. Ils soutiennent les hormones et le système immunitaire."
        },
        {
          name: "Oméga-3",
          definition: "Acides gras essentiels pour le cerveau, le cœur et la réduction de l'inflammation. Présents dans les poissons gras, les noix, les graines."
        },
        {
          name: "Probiotiques",
          definition: "Bonnes bactéries qui soutiennent la santé intestinale, l'immunité et la digestion. On les trouve dans les aliments fermentés."
        }
      ]
    },
    hormones: {
      title: "Hormones & métabolisme",
      intro: "Les hormones régulent de nombreuses fonctions essentielles du corps féminin.",
      terms: [
        {
          name: "Hormones",
          definition: "Messagers chimiques du corps qui régulent de nombreuses fonctions : faim, énergie, humeur, reproduction, métabolisme."
        },
        {
          name: "Insuline",
          definition: "Hormone qui régule le taux de sucre dans le sang. Un déséquilibre peut entraîner fatigue, prise de poids et résistance à l'insuline."
        },
        {
          name: "Métabolisme",
          definition: "Ensemble des réactions chimiques qui permettent au corps de produire de l'énergie à partir des aliments. Il peut être rapide ou lent."
        },
        {
          name: "Œstrogènes",
          definition: "Hormones féminines essentielles pour le cycle menstruel, la fertilité, la peau et les os. Leur déséquilibre affecte le poids et l'humeur."
        },
        {
          name: "Progestérone",
          definition: "Hormone féminine qui prépare le corps à la grossesse et régule le cycle. Sa baisse peut causer rétention d'eau et irritabilité."
        },
        {
          name: "Cortisol",
          definition: "Hormone du stress produite par les glandes surrénales. En excès, elle favorise la prise de poids abdominale et la fatigue."
        },
        {
          name: "Thyroïde",
          definition: "Glande qui produit des hormones régulant le métabolisme, l'énergie et le poids. Un dysfonctionnement entraîne fatigue ou prise de poids."
        }
      ]
    },
    intestin: {
      title: "Intestin & microbiote",
      intro: "L'intestin joue un rôle central dans la santé globale, l'immunité et l'équilibre hormonal.",
      terms: [
        {
          name: "Microbiote intestinal",
          definition: "Ensemble des milliards de bactéries présentes dans l'intestin. Il influence la digestion, l'immunité, l'humeur et le poids."
        },
        {
          name: "Flore intestinale",
          definition: "Autre terme pour désigner le microbiote. Une flore équilibrée protège contre les maladies et améliore la digestion."
        },
        {
          name: "Probiotiques",
          definition: "Bonnes bactéries bénéfiques pour la santé intestinale. On les trouve dans le yaourt, le kéfir, la choucroute, le kimchi."
        },
        {
          name: "Prébiotiques",
          definition: "Fibres qui nourrissent les bonnes bactéries intestinales. Présents dans les légumes, les fruits, les céréales complètes."
        },
        {
          name: "Candida",
          definition: "Levure naturellement présente dans l'intestin. En excès, elle peut déséquilibrer le microbiote et causer ballonnements, fatigue, envies de sucre."
        },
        {
          name: "Dysbiose",
          definition: "Déséquilibre du microbiote intestinal. Elle peut entraîner troubles digestifs, fatigue, inflammation et prise de poids."
        }
      ]
    },
    nutrition: {
      title: "Nutrition & alimentation",
      intro: "Comprendre la différence entre nutrition et alimentation aide à mieux choisir ce que l'on met dans son assiette.",
      terms: [
        {
          name: "Nutrition",
          definition: "Science qui étudie les effets des aliments et des nutriments sur le corps, la santé et les maladies."
        },
        {
          name: "Alimentation",
          definition: "Ce que nous mangeons au quotidien : repas, aliments, habitudes alimentaires. C'est l'acte de se nourrir."
        },
        {
          name: "Nutriment",
          definition: "Élément contenu dans les aliments, utilisé par le corps pour fonctionner : protéines, glucides, lipides, vitamines, minéraux."
        },
        {
          name: "Index glycémique (IG)",
          definition: "Mesure de la vitesse à laquelle un aliment augmente le taux de sucre dans le sang. Un IG bas est préférable pour éviter les pics de glycémie."
        },
        {
          name: "Glycémie",
          definition: "Taux de sucre (glucose) dans le sang. Une glycémie stable est essentielle pour l'énergie, l'humeur et le poids."
        },
        {
          name: "Inflammation",
          definition: "Réaction naturelle du corps face à une agression. Chronique, elle favorise maladies, fatigue et prise de poids."
        },
        {
          name: "Alimentation anti-inflammatoire",
          definition: "Alimentation riche en fruits, légumes, oméga-3, épices (curcuma, gingembre) qui réduit l'inflammation dans le corps."
        }
      ]
    },
    conditions: {
      title: "Conditions & problématiques féminines",
      intro: "Certaines conditions de santé nécessitent une approche nutritionnelle spécifique.",
      terms: [
        {
          name: "SOPK (Syndrome des Ovaires Polykystiques)",
          definition: "Déséquilibre hormonal fréquent chez les femmes, causant cycles irréguliers, acné, prise de poids, résistance à l'insuline."
        },
        {
          name: "Résistance à l'insuline",
          definition: "Les cellules deviennent moins sensibles à l'insuline, entraînant fatigue, prise de poids, envies de sucre et risque de diabète."
        },
        {
          name: "Préménopause",
          definition: "Phase de transition avant la ménopause (40-50 ans), marquée par fluctuations hormonales, prise de poids, bouffées de chaleur."
        },
        {
          name: "Ménopause",
          definition: "Arrêt définitif des règles, généralement vers 50 ans. Baisse des œstrogènes, changements métaboliques, prise de poids possible."
        },
        {
          name: "Dominance en œstrogènes",
          definition: "Excès d'œstrogènes par rapport à la progestérone. Cause prise de poids, rétention d'eau, seins sensibles, irritabilité."
        },
        {
          name: "Hypothyroïdie",
          definition: "Thyroïde qui produit trop peu d'hormones. Symptômes : fatigue, prise de poids, frilosité, constipation, dépression."
        }
      ]
    }
  };

  return (
    <>
      <SEO 
        title="Glossaire Nutrition & Santé Féminine | Nutri Centre Riviera Vevey"
        description="Comprendre les termes essentiels de la nutrition, des hormones et du métabolisme féminin. Glossaire simple et accessible pour mieux comprendre votre corps."
        keywords="glossaire nutrition, hormones définition, métabolisme femme, microbiote intestinal, SOPK, comprendre nutrition, termes médicaux simples, santé féminine"
        url="https://nutricentreriviera.ch/glossaire"
      />
      
      <div style={{ marginTop: '100px' }}>
        {/* Hero Section */}
        <section style={{
          background: 'var(--gradient-hero)',
          padding: '4rem 0',
          textAlign: 'center'
        }}>
          <div className="container">
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '4rem',
              height: '4rem',
              borderRadius: '1rem',
              background: 'white',
              marginBottom: '1.5rem'
            }}>
              <BookOpen size={32} style={{ color: 'var(--accent-primary)' }} />
            </div>
            <h1 className="heading-hero" style={{ marginBottom: '1.5rem' }}>
              Glossaire de la nutrition & santé féminine
            </h1>
            <p className="heading-subtitle" style={{ maxWidth: '900px', margin: '0 auto' }}>
              Comprendre les termes essentiels pour mieux prendre soin de votre santé
            </p>
          </div>
        </section>

        {/* Introduction */}
        <section className="section" style={{ background: 'white' }}>
          <div className="container" style={{ maxWidth: '800px' }}>
            <div style={{
              background: 'var(--accent-primary-light)',
              borderLeft: '4px solid var(--accent-primary)',
              borderRadius: '0.5rem',
              padding: '2rem',
              marginBottom: '3rem'
            }}>
              <p className="body-large" style={{ marginBottom: '1rem', lineHeight: '1.8' }}>
                Aujourd'hui, nous recevons énormément d'informations sur la nutrition, les hormones et la santé, 
                ce qui peut vite devenir confus.
              </p>
              <p className="body-large" style={{ marginBottom: '1rem', lineHeight: '1.8' }}>
                L'objectif de ce glossaire est de <strong>simplifier les termes essentiels</strong> pour mieux comprendre 
                son corps, son métabolisme et faire des choix éclairés.
              </p>
              <p className="body-large" style={{ lineHeight: '1.8' }}>
                <strong>Comprendre les bons mots, c'est déjà faire un premier pas vers une meilleure santé.</strong>
              </p>
            </div>

            {/* Table of Contents - Quick Navigation */}
            <div style={{
              background: 'white',
              border: '1px solid var(--border-light)',
              borderRadius: '0.75rem',
              padding: '2rem',
              marginBottom: '2rem'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '1.5rem', color: 'var(--accent-primary)' }}>
                Navigation rapide
              </h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '1rem'
              }}>
                {Object.entries(glossaryData).map(([key, section]) => (
                  <a
                    key={key}
                    href={`#${key}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.75rem 1rem',
                      background: 'var(--bg-page)',
                      borderRadius: '0.5rem',
                      color: 'var(--text-primary)',
                      textDecoration: 'none',
                      fontSize: '0.95rem',
                      fontWeight: 500,
                      transition: 'all 0.2s ease',
                      border: '1px solid transparent'
                    }}
                    className="toc-link"
                  >
                    <ArrowRight size={16} style={{ color: 'var(--accent-primary)' }} />
                    {section.title}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Glossary Sections */}
        {Object.entries(glossaryData).map(([key, section], sectionIndex) => (
          <section 
            id={key}
            key={key}
            className="section" 
            style={{ 
              background: sectionIndex % 2 === 0 ? 'var(--bg-page)' : 'white'
            }}
          >
            <div className="container" style={{ maxWidth: '900px' }}>
              <h2 className="heading-2" style={{ 
                marginBottom: '1rem',
                color: 'var(--accent-primary)'
              }}>
                {section.title}
              </h2>
              <p className="body-large" style={{ 
                marginBottom: '2rem',
                color: 'var(--text-muted)'
              }}>
                {section.intro}
              </p>

              <div style={{
                display: 'grid',
                gap: '1.5rem'
              }}>
                {section.terms.map((term, index) => (
                  <div 
                    key={index}
                    style={{
                      background: sectionIndex % 2 === 0 ? 'white' : 'var(--bg-page)',
                      borderRadius: '0.75rem',
                      padding: '1.5rem',
                      border: '1px solid var(--border-light)',
                      transition: 'all 0.3s ease'
                    }}
                    className="glossary-term"
                  >
                    <h3 className="heading-3" style={{ 
                      marginBottom: '0.75rem',
                      color: 'var(--text-primary)'
                    }}>
                      {term.name}
                    </h3>
                    <p className="body-medium" style={{ 
                      color: 'var(--text-muted)',
                      lineHeight: '1.6'
                    }}>
                      {term.definition}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        ))}

        {/* CTA Section */}
        <section style={{
          background: 'var(--accent-primary)',
          color: 'white',
          padding: '4rem 0'
        }}>
          <div className="container" style={{ textAlign: 'center' }}>
            <h2 className="heading-2" style={{ color: 'white', marginBottom: '1rem' }}>
              Besoin d'un accompagnement personnalisé ?
            </h2>
            <p className="body-large" style={{ 
              color: 'white', 
              opacity: 0.95, 
              maxWidth: '700px', 
              margin: '0 auto 2rem' 
            }}>
              Nos spécialistes vous accompagnent pour comprendre votre corps, 
              identifier vos déséquilibres et retrouver votre équilibre naturel.
            </p>
            <a
              href="https://www.onedoc.ch/fr/cabinet-de-groupe/vevey/ebdj4/nutri-centre-riviera"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
              style={{ 
                background: 'white', 
                color: 'var(--accent-primary)',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              Prendre rendez-vous au centre
              <ArrowRight size={20} />
            </a>
          </div>
        </section>

        <style>{`
          .glossary-term:hover {
            transform: translateX(8px);
            box-shadow: 0 4px 12px rgba(61, 84, 70, 0.15);
            border-color: var(--accent-primary);
          }
          .toc-link:hover {
            background: var(--accent-primary-light);
            border-color: var(--accent-primary);
            transform: translateX(4px);
          }
        `}</style>
      </div>
    </>
  );
};

export default Glossaire;
